import { Form, Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createBankAccount } from "@/features/banking/api";
import {
  BANK_ACCOUNT_TYPE_OPTIONS,
  type BankAccount,
  type BankAccountType,
} from "@/features/banking/types";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  name: Yup.string().trim().required("Account name is required"),
  institution: Yup.string(),
  // Display-only identifier: store the last 4 digits, never the full number.
  account_last4: Yup.string().matches(/^\d{0,4}$/, "Up to 4 digits"),
  account_type: Yup.string().required("Account type is required"),
});

interface AddAccountValues {
  name: string;
  institution: string;
  account_last4: string;
  account_type: BankAccountType;
}

const initialValues: AddAccountValues = {
  name: "",
  institution: "",
  account_last4: "",
  account_type: "CHECKING",
};

interface AddAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Called with the created account so the parent can append + select it. */
  onCreated: (account: BankAccount) => void;
}

export function AddAccountDialog({
  open,
  onOpenChange,
  clientId,
  onCreated,
}: AddAccountDialogProps) {
  async function handleSubmit(values: AddAccountValues) {
    try {
      const account = await createBankAccount(clientId, {
        name: values.name.trim(),
        institution: values.institution.trim() || undefined,
        account_last4: values.account_last4 || undefined,
        account_type: values.account_type,
      });
      toast.success("Bank account added");
      onCreated(account);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the account.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add bank account</DialogTitle>
          <DialogDescription>
            Record a client bank account to attach transactions to.
          </DialogDescription>
        </DialogHeader>
        <Formik<AddAccountValues>
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField
                name="name"
                label="Account name"
                placeholder="e.g. Operating Checking"
                autoFocus
              />
              <FormField name="institution" label="Institution" placeholder="e.g. Chase" />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="account_last4"
                  label="Last 4 digits"
                  placeholder="1234"
                  inputMode="numeric"
                  maxLength={4}
                />
                <SelectField
                  name="account_type"
                  label="Account type"
                  options={BANK_ACCOUNT_TYPE_OPTIONS}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add account"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
